import type { MarketPayload, TimeGrain, TrendPoint } from "../shared/market";
import type { ModelEvent, Preset, WeeklyAnalysis } from "./generated/snapshot";

const DAY_MS = 86_400_000;

export function shiftDay(day: string, amount: number): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}
export function daysBetween(start: string, end: string): number {
  return Math.floor((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / DAY_MS) + 1;
}

export interface PositionedModelEvent {
  event: ModelEvent;
  position: number;
  lane: number;
  showLabel: boolean;
}

export function layoutModelEvents(
  events: ModelEvent[],
  start: string,
  end: string,
  labelWidthRatio = 0.12,
  laneCount = 2,
): PositionedModelEvent[] {
  const total = Math.max(1, daysBetween(start, end) - 1);
  const visible = events
    .filter((event) => event.date >= start && event.date <= end)
    .map((event) => ({
      event,
      position: Math.max(0, Math.min(1, (daysBetween(start, event.date) - 1) / total)),
    }));
  const intervals: Array<Array<[number, number]>> = Array.from({ length: laneCount }, () => []);
  const placements = new Map<string, { lane: number; showLabel: boolean }>();
  const prioritized = [...visible].sort((a, b) => {
    const importance = Number(b.event.importance === "major") - Number(a.event.importance === "major");
    return importance || a.event.date.localeCompare(b.event.date);
  });

  for (const item of prioritized) {
    const left = Math.max(0, Math.min(1 - labelWidthRatio, item.position - labelWidthRatio / 2));
    const right = left + labelWidthRatio;
    const lane = intervals.findIndex((entries) => entries.every(([from, to]) => right + 0.01 < from || left - 0.01 > to));
    if (lane === -1) {
      placements.set(item.event.id, { lane: 0, showLabel: false });
      continue;
    }
    intervals[lane].push([left, right]);
    placements.set(item.event.id, { lane, showLabel: true });
  }

  return visible
    .sort((a, b) => a.event.date.localeCompare(b.event.date))
    .map((item) => ({ ...item, ...(placements.get(item.event.id) ?? { lane: 0, showLabel: false }) }));
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function delta(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous <= 0) return null;
  return ((current - previous) / previous) * 100;
}

function startOfWeek(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  const weekday = date.getUTCDay() || 7;
  return shiftDay(day, -(weekday - 1));
}

function bucket(point: TrendPoint, grain: TimeGrain): { key: string; label: string } {
  const day = point.startDate;
  if (grain === "day") return { key: day, label: day.slice(5) };
  if (grain === "week") {
    const start = startOfWeek(day);
    return { key: start, label: `${start.slice(5)} 周` };
  }
  if (grain === "month") return { key: day.slice(0, 7), label: day.slice(0, 7) };
  const quarter = Math.floor((Number(day.slice(5, 7)) - 1) / 3) + 1;
  return { key: `${day.slice(0, 4)}-Q${quarter}`, label: `${day.slice(0, 4)} Q${quarter}` };
}

export function aggregateTrends(points: TrendPoint[], grain: TimeGrain): TrendPoint[] {
  if (grain === "day") return points;
  const groups = new Map<string, { label: string; points: TrendPoint[] }>();
  for (const point of points) {
    const group = bucket(point, grain);
    const current = groups.get(group.key) ?? { label: group.label, points: [] };
    current.points.push(point);
    groups.set(group.key, current);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, group]) => {
    const totalTokens = sum(group.points.map((point) => point.totalTokens));
    const pricedTokens = sum(group.points.map((point) => point.pricedTokens));
    const estimatedRetailValue = sum(group.points.flatMap((point) => point.estimatedRetailValue === null ? [] : [point.estimatedRetailValue]));
    return {
      key,
      label: group.label,
      startDate: group.points.at(0)!.startDate,
      endDate: group.points.at(-1)!.endDate,
      totalTokens,
      pricedTokens,
      blendedPricePerMillion: pricedTokens > 0 ? (estimatedRetailValue / pricedTokens) * 1_000_000 : null,
      estimatedRetailValue: pricedTokens > 0 ? estimatedRetailValue : null,
      pricedTokenShare: totalTokens > 0 ? pricedTokens / totalTokens : 0,
    };
  });
}

function summarize(points: TrendPoint[]) {
  const tokenTotal = sum(points.map((point) => point.totalTokens));
  const pricedTokens = sum(points.map((point) => point.pricedTokens));
  const value = sum(points.flatMap((point) => point.estimatedRetailValue === null ? [] : [point.estimatedRetailValue]));
  return {
    tokenTotal,
    pricedTokens,
    price: pricedTokens > 0 ? (value / pricedTokens) * 1_000_000 : null,
    value: pricedTokens > 0 ? value : null,
  };
}

export function viewForPreset(source: MarketPayload, grain: TimeGrain): MarketPayload {
  return { ...source, meta: { ...source.meta, grain }, trends: aggregateTrends(source.trends, grain) };
}

export function viewForRange(all: MarketPayload, start: string, end: string, grain: TimeGrain): MarketPayload {
  const currentDaily = all.trends.filter((point) => point.startDate >= start && point.endDate <= end);
  const periodDays = daysBetween(start, end);
  const previousEnd = shiftDay(start, -1);
  const previousStart = shiftDay(previousEnd, -(periodDays - 1));
  const previousDaily = all.trends.filter((point) => point.startDate >= previousStart && point.endDate <= previousEnd);
  const current = summarize(currentDaily);
  const previous = summarize(previousDaily);
  const pricedTokenShare = current.tokenTotal > 0 ? current.pricedTokens / current.tokenTotal : 0;
  return {
    ...all,
    meta: {
      ...all.meta,
      rangeStart: start,
      rangeEnd: end,
      previousStart,
      previousEnd,
      grain,
      pricedTokenShare,
    },
    analysis: { ...all.analysis, headline: "", bullets: [] },
    headline: {
      tokenTotal: current.tokenTotal,
      tokenDeltaPct: delta(current.tokenTotal, previous.tokenTotal),
      blendedPricePerMillion: current.price,
      priceDeltaPct: delta(current.price, previous.price),
      estimatedRetailValue: current.value,
      valueDeltaPct: delta(current.value, previous.value),
    },
    trends: aggregateTrends(currentDaily, grain),
    modelShares: [],
  };
}

function pct(value: number | null): string {
  if (value === null) return "暂无完整基期";
  return `${value >= 0 ? "增长" : "下降"} ${Math.abs(value).toFixed(1)}%`;
}

function compact(value: number | null): string {
  if (value === null) return "暂无";
  return new Intl.NumberFormat("zh-CN", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

export function rangeAnalysis(data: MarketPayload, events: ModelEvent[]): WeeklyAnalysis {
  const tokenDirection = data.headline.tokenDeltaPct !== null && data.headline.tokenDeltaPct >= 0 ? "扩张" : "回落";
  const priceDirection = data.headline.priceDeltaPct !== null && data.headline.priceDeltaPct >= 0 ? "上行" : "下行";
  const inRange = events.filter((event) => event.date >= (data.meta.rangeStart ?? "") && event.date <= (data.meta.rangeEnd ?? ""));
  return {
    eyebrow: "自选区间 · 即时事实摘要",
    headline: `所选区间需求${tokenDirection}，价格${priceDirection}；金额变化仍主要取决于两者的共同作用。`,
    summary: `从 ${data.meta.rangeStart} 到 ${data.meta.rangeEnd}，样本共记录 ${compact(data.headline.tokenTotal)} Token。该结论由浏览器基于页面内置数据即时计算，不替代每周生成的深度解读。`,
    evidence: [
      `Token 较前一等长区间${pct(data.headline.tokenDeltaPct)}。`,
      `加权零售价${pct(data.headline.priceDeltaPct)}，可定价覆盖率 ${(data.meta.pricedTokenShare * 100).toFixed(1)}%。`,
      `牌价情景下的估算金额${pct(data.headline.valueDeltaPct)}。`,
    ],
    eventReading: inRange.length > 0
      ? `区间内有 ${inRange.length} 个重大模型事件：${inRange.slice(-3).map((event) => event.model).join("、")}。它们是时间参照，不自动构成因果解释。`
      : "区间内没有收录的重大模型发布点；这不代表期间没有产品更新。",
    caveat: "即时摘要只陈述可计算事实；完整 AI 解读在 30 天、90 天、1 年和全部历史预设中每周更新。",
    generatedAt: data.meta.lastSync ?? "",
  };
}

export function presetFromRange(start: string, end: string, availableStart: string, availableEnd: string): Preset | null {
  if (start === availableStart && end === availableEnd) return "all";
  const days = daysBetween(start, end);
  if (end !== availableEnd) return null;
  if (days === 30) return "30d";
  if (days === 90) return "90d";
  if (days >= 364 && days <= 366) return "1y";
  return null;
}
